import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useExchangeStripeConnectMutation,
  useListenStripePubKeyQuery,
  useListenStripeProductIdQuery,
  useListenStripeProductNameQuery,
  useListenProductCurrencyQuery,
} from '../productInfoApi'
import { Spinner } from 'common/components/Spinner/Spinner'
import { GenerateStripeProduct } from '../components/GenerateStripeProduct/GenerateStripeProduct'
import { ConnectStripe } from '../components/ConnectStripe/ConnectStripe'
import { DisconnectStripe } from '../components/DisconnectStripe/DisconnectStripe'
import { ProductInfoForm } from '../components/ProductInfoForm/ProductInfoForm'
import { getProductCurrency } from '../utils/utils'
import { useListenAllOrgSlugsQuery } from 'modules/PageBuilder/pageBuilderApi'
import { useListenCoachOrgNameQuery } from 'modules/Account/accountApi'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import {
  STRIPE_CONNECT_RETURN_PARAMS,
  getStripeConnectErrorMessage,
  getStripeConnectExchangeErrorReason,
} from '../utils/stripeConnect'

export const STRIPE_CONNECTED_TEXT = 'Your Stripe account is connected. Now set up your product.'
export const STRIPE_CONNECT_FINISHING_TEXT = 'Finishing your Stripe connection...'

export default function ProductInfo() {
  const { userId } = useAuth()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const [isGeneratingProduct, setIsGeneratingProduct] = useState(false)
  const { createAlert } = useAlert()
  const [searchParams, setSearchParams] = useSearchParams()

  // Stripe sends the coach back here with `?code=...&scope=...&state=...`, or
  // `?error=access_denied&...` when they backed out. The parameters are
  // dropped first, so a refresh or back-navigation cannot replay the code.
  // Then the code and state go to the callable, which does the exchange and
  // writes productInfo; the live listener on the publishable key takes it
  // from there.
  const [exchangeStripeConnect] = useExchangeStripeConnectMutation()
  const [isFinishingConnect, setIsFinishingConnect] = useState(false)
  const stripeCode = searchParams.get('code')
  const stripeState = searchParams.get('state')
  const stripeError = searchParams.get('error')
  useEffect(() => {
    if (!stripeCode && !stripeError) return

    const nextParams = new URLSearchParams(searchParams)
    STRIPE_CONNECT_RETURN_PARAMS.forEach((param) => nextParams.delete(param))
    setSearchParams(nextParams, { replace: true })

    if (stripeError) {
      createAlert({ text: getStripeConnectErrorMessage(stripeError), type: 'danger' })
      return
    }

    setIsFinishingConnect(true)
    exchangeStripeConnect({ code: stripeCode, state: stripeState }).then(({ error }) => {
      setIsFinishingConnect(false)
      if (error) {
        console.error(error)
        createAlert({ text: getStripeConnectErrorMessage(getStripeConnectExchangeErrorReason(error)), type: 'danger' })
      } else {
        createAlert({ text: STRIPE_CONNECTED_TEXT, type: 'success' })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeCode, stripeState, stripeError])

  const { data: stripePubKey } = useListenStripePubKeyQuery({ coachOrgId })
  const stripePubKeyLoading = stripePubKey === undefined || stripePubKey?.isLoading
  const stripePubKeyExists = !stripePubKeyLoading && Boolean(stripePubKey)

  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading

  const { data: stripeProductName } = useListenStripeProductNameQuery({ coachOrgId })
  const stripeProductNameLoading = stripeProductName === undefined || stripeProductName?.isLoading

  const { data: currencyData } = useListenProductCurrencyQuery({ coachOrgId })
  const currencyLoading = currencyData === undefined || currencyData?.isLoading

  const { data: allOrgSlugsData } = useListenAllOrgSlugsQuery()
  const coachOrgSlugLoading = allOrgSlugsData === undefined || allOrgSlugsData?.isLoading
  const allOrgSlugs = allOrgSlugsData || {}
  const { data: coachOrgName } = useListenCoachOrgNameQuery(coachOrgId)
  const coachOrgNameLoading = coachOrgName === undefined || coachOrgName?.isLoading

  const isLoading =
    stripePubKeyLoading ||
    stripeProductNameLoading ||
    stripeProductIdLoading ||
    currencyLoading ||
    coachOrgSlugLoading | coachOrgNameLoading

  // While the callable is finishing the connection the coach must not be
  // offered the Connect button again, so that state shares the loading view.
  if (isLoading || isFinishingConnect) {
    return (
      <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
        <div className={`w-full mx-auto ${stripePubKeyExists ? 'max-w-3xl mt-6 md:mt-12' : 'max-w-xl mt-24'}`}>
          {stripePubKeyExists && (
            <h1 className='font-bold text-3xl md:text-5xl text-tBlack capitalize mb-6 pb-12'>Settings</h1>
          )}
          <div className='bg-white rounded-xl p-8 h-96 flex flex-col items-center justify-center border-2 border-gray-200'>
            <Spinner className='w-10 h-10 text-gray-100' />
            {isFinishingConnect && <p className='mt-4 text-gray-500'>{STRIPE_CONNECT_FINISHING_TEXT}</p>}
          </div>
        </div>
      </div>
    )
  }

  if (!stripePubKeyExists) {
    return (
      <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
        <div className='w-full max-w-xl mx-auto mt-24'>
          <div className='bg-white rounded-xl p-8 border-2 border-gray-200'>
            <ConnectStripe coachOrgId={coachOrgId} email={profile?.email} />
          </div>
        </div>
      </div>
    )
  }

  if (!stripeProductId || isGeneratingProduct) {
    return (
      <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
        <div className='w-full max-w-3xl mx-auto mt-6 md:mt-12'>
          <h1 className='font-bold text-3xl md:text-5xl text-tBlack capitalize mb-6'>Settings</h1>
          <FormRefsControlProvider>
            <div className='mt-12 bg-white rounded-xl p-8 border-2 border-gray-200'>
              <GenerateStripeProduct
                coachOrgId={coachOrgId}
                allOrgSlugs={allOrgSlugs}
                coachOrgName={coachOrgName}
                isGeneratingProduct={isGeneratingProduct}
                setIsGeneratingProduct={setIsGeneratingProduct}
              />
            </div>
          </FormRefsControlProvider>
          <DisconnectStripe coachOrgId={coachOrgId} />
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
      <div className='w-full max-w-3xl mx-auto mt-6 md:mt-12'>
        <h1 className='font-bold text-3xl md:text-5xl text-tBlack capitalize mb-6'>Settings</h1>
        <FormRefsControlProvider>
          <div className='mt-12 bg-white rounded-xl p-8 border-2 border-gray-200'>
            <ProductInfoForm
              coachOrgId={coachOrgId}
              stripeProductId={stripeProductId}
              stripeProductName={stripeProductName}
              currency={getProductCurrency({ defaultCurrency: currencyData })}
              allOrgSlugs={allOrgSlugs}
            />
          </div>
        </FormRefsControlProvider>
        <DisconnectStripe coachOrgId={coachOrgId} />
      </div>
    </div>
  )
}
